import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Reservation, ReservationPaxGroup, ServiceBooking } from '@turizi/shared';
import {
    IconUsers,
    IconEdit,
    IconTrash,
    IconPlaylistAdd,
    IconPlaylistX,
    IconPlus,
    IconAlertCircle,
    IconFilter,
    IconFilterOff,
    IconUsersGroup,
    IconArrowsSplit2,
    IconEye,
    IconEyeOff,
    IconLinkPlus,
} from '@tabler/icons-react';
import { Button, ActionIcon, Menu, Tooltip, Loader } from '@mantine/core';
import PaxGroupModal from './pax-group-modal';
import PaxDistributionModal from './pax-distribution-modal';
import PaxGroupAssignBookingsModal from './pax-group-assign-bookings-modal';
import { notify } from '@/lib/notify';
import {
    useReservationPaxGroups,
    useCreateReservationPaxGroup,
    useUpdateReservationPaxGroup,
    useDeleteReservationPaxGroup,
    useAddPaxGroupToAllBookings,
    useRemovePaxGroupFromAllBookings,
} from '@/hooks/api-hooks';
import { BookingPaxsSize, ShowPaxGroup, ShowPerson } from '../snippets';
import { sortPersons } from '@/lib/format-utils';

interface PaxGroupListProps {
    reservation: Reservation
    activeFilterId?: string | null;
    onFilterClick?: (id: string | null) => void;
    editMode?: boolean;
}

export default function PaxGroupList({
    reservation,
    activeFilterId,
    onFilterClick,
    editMode = true,
}: PaxGroupListProps) {
    const { t } = useTranslation();
    const reservationId = reservation.id!;
    const bookings = reservation.service_bookings || [];

    const {
        data: paxGroups = [],
        isLoading
    } = useReservationPaxGroups(reservationId);
    const createMutation = useCreateReservationPaxGroup();
    const updateMutation = useUpdateReservationPaxGroup();
    const deleteMutation = useDeleteReservationPaxGroup();
    const addToAllMutation = useAddPaxGroupToAllBookings();
    const removeFromAllMutation = useRemovePaxGroupFromAllBookings();

    const [modalOpened, setModalOpened] = useState(false);
    const [editingGroup, setEditingGroup] = useState<ReservationPaxGroup | null>(null);
    const [distributeModalOpened, setDistributeModalOpened] = useState(false);
    const [distributeGroupId, setDistributeGroupId] = useState<number | null>(null);
    const [assignBookingsGroup, setAssignBookingsGroup] = useState<ReservationPaxGroup | null>(null);
    const [expandedPersonGroupIds, setExpandedPersonGroupIds] = useState<Record<number, boolean>>({});
    const [showPaxDetails, setShowPaxDetails] = useState(false);
    const [activeMenu, setActiveMenu] = useState<{ groupId: number; x: number; y: number } | null>(null);

    const handleOpenCreate = () => {
        setEditingGroup(null);
        setModalOpened(true);
    };

    const handleOpenEdit = (group: ReservationPaxGroup) => {
        setEditingGroup(group);
        setModalOpened(true);
    };

    const handleOpenDistribute = (groupId?: number | null) => {
        setDistributeGroupId(groupId || null);
        setDistributeModalOpened(true);
    };

    const hasBookingsWithOrders = (groupId?: number) => {
        const targetBookings = groupId
            ? bookings.filter((b) => b.pax_groups?.some((bg) => bg.pax_group_id === groupId))
            : bookings;
        return targetBookings.some((b) => (b.service_order_bookings?.length || 0) > 0);
    };

    const handleSave = async (data: Partial<ReservationPaxGroup>) => {
        try {
            if (editingGroup?.id) {
                updateMutation.mutate({
                    reservationId,
                    groupId: editingGroup.id,
                    data,
                });
            } else {
                createMutation.mutate({
                    reservationId,
                    data,
                });
            }
            setModalOpened(false);
            setEditingGroup(null);
        } catch (error) {
            console.error('Error saving pax group:', error);
            notify.error(t('error'), editingGroup?.id ? t('pax_group_update_failed') : t('pax_group_create_failed'));
        }
    };

    const handleDelete = async (groupId: number) => {
        if (!window.confirm(t('delete_pax_group_confirm'))) return;
        if (hasBookingsWithOrders(groupId) && !window.confirm(t('confirm_modify_booking_with_service_order'))) return;

        try {
            await deleteMutation.mutateAsync({
                reservationId,
                groupId,
            });
            notify.success(t('success'), t('pax_group_deleted_successfully'));
            setModalOpened(false);
            setEditingGroup(null);
        } catch (error) {
            console.error('Error deleting pax group:', error);
            notify.error(t('error'), t('pax_group_delete_failed'));
        }
    };

    const handleAddToAll = async (groupId: number) => {
        if (hasBookingsWithOrders() && !window.confirm(t('confirm_modify_booking_with_service_order'))) return;
        try {
            await addToAllMutation.mutateAsync({
                reservationId,
                groupId,
            });
            notify.success(t('success'), t('pax_group_added_to_all_bookings'));
        } catch (error) {
            console.error('Error adding pax group to all bookings:', error);
            notify.error(t('error'), t('error'));
        }
    };

    const handleRemoveFromAll = async (groupId: number) => {
        if (hasBookingsWithOrders(groupId) && !window.confirm(t('confirm_modify_booking_with_service_order'))) return;
        try {
            await removeFromAllMutation.mutateAsync({
                reservationId,
                groupId,
            });
            notify.success(t('success'), t('pax_group_removed_from_all_bookings'));
        } catch (error) {
            console.error('Error removing pax group from all bookings:', error);
            notify.error(t('error'), t('error'));
        }
    };

    if (isLoading) {
        return (
            <div className='flex items-center justify-center'>
                <Loader size='sm' color='black' />
            </div>
        )
    }

    return (
        <div className="space-y-2">
            {/* Header with Add Button */}
            <div className="flex items-center justify-between pb-1 flex-wrap gap-1">
                <div className="flex items-center gap-1.5">
                    <IconUsers size={16} className="text-gray-500" />
                    <span className="text-xs font-semibold text-gray-700">
                        {t('paxs')}
                    </span>
                    {paxGroups.length > 0 && (
                        <Tooltip label={showPaxDetails ? t('hide_pax_details') : t('show_pax_details')} withArrow>
                            <ActionIcon
                                size="xs"
                                variant={showPaxDetails ? 'light' : 'subtle'}
                                color={showPaxDetails ? 'indigo' : 'gray'}
                                onClick={() => setShowPaxDetails((prev) => !prev)}
                            >
                                {showPaxDetails ? <IconEye size={13} /> : <IconEyeOff size={13} />}
                            </ActionIcon>
                        </Tooltip>
                    )}
                </div>
                {editMode && (
                    <div className="flex items-center gap-1">
                        <Button
                            size="compact-xs"
                            variant="subtle"
                            color="indigo"
                            leftSection={<IconPlus size={12} />}
                            onClick={handleOpenCreate}
                        >
                            {t('add_paxs')}
                        </Button>
                    </div>
                )}
            </div>
            <div className="space-y-1.5">
                {[...paxGroups]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((group) => {
                        const filterKey = `group-${group.id}`;
                        const isActive = activeFilterId === filterKey;

                        // Count how many bookings contain this pax group
                        const linkedBookingsCount = bookings.filter((b) =>
                            b.pax_groups?.some((bg) => bg.pax_group_id === group.id)
                        ).length;

                        const sortedPersons = sortPersons(group.persons);
                        const isPersonsExpanded = Boolean(group.id && expandedPersonGroupIds[group.id]);
                        const visiblePersons = isPersonsExpanded ? sortedPersons : sortedPersons.slice(0, 5);
                        const hiddenPersonsCount = Math.max(0, sortedPersons.length - visiblePersons.length);

                        const showGroupSize =
                            sortedPersons.length !== 1 ||
                            BookingPaxsSize(group) !== BookingPaxsSize(sortedPersons[0]);

                        const isMenuOpen = Boolean(group.id && activeMenu?.groupId === group.id);
                        const canOpenMenu = Boolean((editMode || onFilterClick) && group.id);

                        return (
                            <Menu
                                key={group.id}
                                opened={isMenuOpen}
                                onChange={(opened) => {
                                    if (!opened) setActiveMenu(null);
                                }}
                                position="bottom-start"
                                offset={4}
                                shadow="md"
                                radius="md"
                                width={240}
                                disabled={!canOpenMenu}
                            >
                                <div
                                    onMouseDown={(e) => {
                                        if (isMenuOpen) {
                                            e.stopPropagation();
                                        }
                                    }}
                                    onClick={(e) => {
                                        if (!canOpenMenu || !group.id) return;
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setActiveMenu((prev) =>
                                            prev?.groupId === group.id
                                                ? null
                                                : {
                                                    groupId: group.id!,
                                                    x: e.clientX - rect.left,
                                                    y: e.clientY - rect.top,
                                                }
                                        );
                                    }}
                                    className={`relative px-3 py-2 rounded-lg border transition-all select-none ${canOpenMenu ? 'cursor-pointer' : ''} ${isActive
                                        ? 'border-blue-400 bg-blue-50/60 shadow-xs ring-1 ring-blue-300'
                                        : isMenuOpen
                                            ? 'border-slate-300 bg-slate-50/80 shadow-xs'
                                            : 'border-slate-200 bg-white hover:bg-slate-50/80 hover:border-slate-300'
                                        }`}
                                >
                                    <Menu.Target>
                                        <span
                                            aria-hidden="true"
                                            className="pointer-events-none absolute h-0 w-0"
                                            style={{
                                                left: isMenuOpen && activeMenu ? activeMenu.x : 0,
                                                top: isMenuOpen && activeMenu ? activeMenu.y : 0,
                                            }}
                                        />
                                    </Menu.Target>

                                    <div className="flex flex-col gap-1 min-w-0">
                                        {/* Name, PAX Count & Unassigned Warning */}
                                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                                            <IconUsersGroup size={14} className='text-indigo-500' />
                                            <span className="font-semibold text-xs text-slate-900 leading-tight">
                                                {group.name}
                                            </span>
                                            {showGroupSize && (
                                                <ShowPaxGroup
                                                    infants={group.infants}
                                                    adults={group.adults}
                                                    children={group.children}
                                                    seniors={group.seniors}
                                                    guests={group.guests}
                                                />
                                            )}
                                            {isActive && (
                                                <Tooltip label={t('clear_filters')} withArrow position="top">
                                                    <span className="inline-flex items-center text-blue-600 shrink-0">
                                                        <IconFilter size={12} />
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {linkedBookingsCount === 0 && (
                                                <Tooltip label={t('not_assigned_to_services')} withArrow position="top">
                                                    <div className='text-xxs font-medium text-gray-600 px-1 py-0.5 rounded-full bg-gray-100'>
                                                        {t('not_assigned')}
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </div>

                                        {/* Paxs list */}
                                        {visiblePersons.map(p => (
                                            <div key={p.id} className="ml-2 min-w-0">
                                                <ShowPerson person={p} showPaxData={showPaxDetails} />
                                            </div>
                                        ))}
                                        {sortedPersons.length > 5 && group.id && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedPersonGroupIds((prev) => ({
                                                        ...prev,
                                                        [group.id!]: !prev[group.id!],
                                                    }));
                                                }}
                                                className="ml-2 text-[11px] font-semibold text-indigo-600 hover:underline w-fit cursor-pointer"
                                            >
                                                {isPersonsExpanded
                                                    ? t('show_less')
                                                    : t('show_more_paxs', { count: hiddenPersonsCount })}
                                            </button>
                                        )}

                                        {/* Observations / Notes */}
                                        {group.observations && (
                                            <p className="text-[11px] text-slate-500 italic mt-0.5 break-words leading-tight">
                                                {group.observations}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <Menu.Dropdown>
                                    {onFilterClick && (
                                        <Menu.Item
                                            leftSection={
                                                isActive ? (
                                                    <IconFilterOff size={14} className="text-blue-600" />
                                                ) : (
                                                    <IconFilter size={14} className="text-slate-600" />
                                                )
                                            }
                                            onClick={() => onFilterClick(isActive ? null : filterKey)}
                                        >
                                            <span className={`font-medium text-xs ${isActive ? 'text-blue-600 font-semibold' : 'text-slate-700'}`}>
                                                {isActive ? t('clear_filters') : t('filter_services')}
                                            </span>
                                        </Menu.Item>
                                    )}

                                    {editMode && (
                                        <>
                                            {onFilterClick && <Menu.Divider />}
                                            <Menu.Item
                                                leftSection={<IconEdit size={14} className="text-slate-600" />}
                                                onClick={() => handleOpenEdit(group)}
                                            >
                                                <span className="font-medium text-xs text-slate-700">{t('edit_pax_group')}</span>
                                            </Menu.Item>

                                            <Menu.Divider />
                                            <Menu.Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                {t('assign_to_bookings')}
                                            </Menu.Label>
                                            <Menu.Item
                                                leftSection={<IconLinkPlus size={14} className="text-indigo-600" />}
                                                onClick={() => setAssignBookingsGroup(group)}
                                                disabled={!group.id || bookings.length === 0}
                                            >
                                                <span className="font-medium text-xs text-slate-700">{t('assign_to_booking')}</span>
                                            </Menu.Item>
                                            <Menu.Item
                                                leftSection={<IconArrowsSplit2 size={14} className="text-teal-600" />}
                                                onClick={() => handleOpenDistribute(group.id)}
                                                disabled={!group.id || bookings.length === 0}
                                            >
                                                <span className="font-medium text-xs text-slate-700">{t('distribute_paxs_across_bookings')}</span>
                                            </Menu.Item>
                                            <Menu.Item
                                                leftSection={<IconPlaylistAdd size={14} className="text-emerald-600" />}
                                                onClick={() => group.id && handleAddToAll(group.id)}
                                                disabled={!group.id || bookings.length === 0 || linkedBookingsCount === bookings.length}
                                            >
                                                <span className="font-medium text-xs text-slate-700">{t('add_to_all_bookings')}</span>
                                            </Menu.Item>
                                            <Menu.Item
                                                leftSection={<IconPlaylistX size={14} className="text-amber-600" />}
                                                onClick={() => group.id && handleRemoveFromAll(group.id)}
                                                disabled={!group.id || linkedBookingsCount === 0}
                                            >
                                                <span className="font-medium text-xs text-slate-700">{t('remove_from_all_bookings')}</span>
                                            </Menu.Item>

                                            <Menu.Divider />
                                            <Menu.Item
                                                color="red"
                                                leftSection={<IconTrash size={14} />}
                                                onClick={() => group.id && handleDelete(group.id)}
                                            >
                                                <span className="font-medium text-xs">{t('delete_pax_group')}</span>
                                            </Menu.Item>
                                        </>
                                    )}
                                </Menu.Dropdown>
                            </Menu>
                        );
                    })}
            </div>

            {/* Modal */}
            {modalOpened && (
                <PaxGroupModal
                    opened={modalOpened}
                    onClose={() => {
                        setModalOpened(false);
                        setEditingGroup(null);
                    }}
                    reservation={reservation}
                    group={editingGroup}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    isLoading={createMutation.isPending || updateMutation.isPending || deleteMutation.isPending}
                />
            )}

            {distributeModalOpened && (
                <PaxDistributionModal
                    opened={distributeModalOpened}
                    onClose={() => {
                        setDistributeModalOpened(false);
                        setDistributeGroupId(null);
                    }}
                    reservationId={reservationId}
                    bookings={bookings}
                    initialGroupId={distributeGroupId}
                />
            )}

            {assignBookingsGroup && (
                <PaxGroupAssignBookingsModal
                    opened={Boolean(assignBookingsGroup)}
                    onClose={() => setAssignBookingsGroup(null)}
                    reservationId={reservationId}
                    group={assignBookingsGroup}
                    bookings={bookings}
                />
            )}
        </div>
    );
}
